<template>
  <div class="service-data">
    <div v-if="!service" class="text-muted p-3">No service selected.</div>
    <div v-else>
      <AddressVerificationReport
        v-if="service?.service_name === 'Address Match'"
        :address_data="payload"
      />
      <CreditReport
        v-else-if="service?.service_name === 'Experian Credit Report (PDF)'"
        :credit_data="payload"
      />
      <ReportViewer
        v-else-if="service?.service_name === 'Enterprise Credit Check'"
        service_type="enterprise_credit"
        :export_data="payload"
        :response_doc="response?.response_doc"
        title="Enterprise Credit Check Report"
      >
        <CompanyCreditReport :customer_credit_data="payload" />
      </ReportViewer>

      <ReportViewer
        v-else-if="
          service?.service_name === 'Alternative Liveness Verification'
        "
        service_type="alternative_liveness"
        :export_data="payload"
        :response_doc="response?.response_doc"
        title="Alternative Liveness Verification Report"
      >
        <AlternativeLivenessReport :verification_data="payload" />
      </ReportViewer>
      <ReportViewer
        v-else-if="service?.service_name === 'Anti Money Laundering'"
        :response_doc="response?.response_doc"
        title="Anti-Money Laundering Report"
        service_type="aml"
        :export_data="payload"
      >
        <AMLReport
          :aml_data="amlNormalized"
          :response_info="amlResponseInfo"
          :date_scored="amlDateScored"
        />
      </ReportViewer>
      <ReportViewer
        v-else-if="service?.service_name === 'Enterprise Anti Money Laundering'"
        service_type="enterprise_aml"
        :export_data="payload"
        :response_doc="response?.response_doc"
        title="Enterprise Anti-Money Laundering Report"
      >
        <EnterpriseAmlVerificationReport :aml_data="payload" />
      </ReportViewer>
      <ReportViewer
        v-else-if="service?.service_name === 'OCR verification'"
        service_type="ocr_verification"
        :export_data="payload"
        :response_doc="response?.response_doc"
        title="OCR Verification Report"
      >
        <OcrVerificationReport :ocr_verification_data="payload" />
      </ReportViewer>
      <ReportViewer
        v-else-if="service?.service_name === 'OCR Liveness Verification'"
        service_type="ocr_liveness"
        :export_data="payload"
        :response_doc="response?.response_doc"
        title="OCR Liveness Verification Report"
      >
        <OcrLivenessVerificationReport :ocr_liveness_data="payload" />
      </ReportViewer>
      <ReportViewer
        v-else-if="service?.service_name === 'ID Verification (Liveness check)'"
        service_type="id_liveness"
        :export_data="payload"
        :response_doc="response?.response_doc"
        title="ID Verification Report"
      >
        <IdVerificationReport
          :id_liveness_data="payload"
          :customer_image="customer_image"
        />
      </ReportViewer>
      <RefugeeVerificationReport
        v-else-if="service?.service_name === 'Refugee Verification'"
        :refugee_verification_data="payload"
      />
      <ReportViewer
        v-else-if="service?.service_name === 'RSA ID Verification'"
        service_type="rsa_id_verification"
        :export_data="payload"
        :response_doc="response?.response_doc"
        title="RSA ID Verification Report"
      >
        <RsaIdVerificationReport :rsa_id_data="payload" />
      </ReportViewer>
      <ReportViewer
        v-else-if="service?.service_name === 'Telephones'"
        service_type="telephones"
        :export_data="payload"
        :response_doc="response?.response_doc"
        title="Telephones"
      >
      </ReportViewer>
      <EligibilitySolutionReport
        v-else-if="service?.service_name === 'Eligibility Solution'"
        :eligibility_data="payload"
      />
      <BankStatementVerificationReport
        v-else-if="
          service?.service_name === 'Online Bank Statements' ||
          service?.service_name === 'Manual Bank Statement Upload'
        "
        :bank_statement_data="payload"
      />
      <AllInOneBankVerificationReport
        v-else-if="
          service?.service_name === 'All in One Bank Statement Retrieval'
        "
        :all_in_one_data="payload"
      />
      <IndividualBankAccountReport
        v-else-if="
          service?.service_name === 'Bank Account Individual Verification'
        "
        :bank_account_data="payload"
      />
      <DirectorScreeningReport
        v-else-if="service?.service_name === 'Director Screening'"
        :bank_account_data="payload"
      />
      <CompanyBankAccountReport
        v-else-if="
          service?.service_name === 'Bank Account Company Verification'
        "
        :company_account_data="payload"
      />
      <TrustSearchReport
        v-else-if="service?.service_name === 'Trust Search'"
        :trust_search_data="payload"
      />
      <MastercardReport
        v-else-if="service?.service_name === 'Mastercard MATCH Pro Inquiry'"
        :mastercard_data="payload"
      />
      <EnterpriseKYBReport
        v-else-if="service?.service_name === 'Enterprise KYB'"
        :kyb_data="payload"
      />
      <ReportViewer
        v-else-if="
          service?.service_name === 'Comprehensive Liveness Verification'
        "
        service_type="comprehensive_liveness"
        :export_data="payload"
        :response_doc="response?.response_doc"
        title="Comprehensive Liveness Verification Report"
      >
        <ComprehensiveLivenessVerificationReport
          :comprehensive_liveness_data="payload"
        />
      </ReportViewer>
      <AcademicCheckManagement
        v-else-if="service?.service_name === 'Academic Post 92 Check'"
        :customerApplicationId="customerApplicationId"
      />
      <HuruCriminalCheckManagement
        v-else-if="service?.service_name === 'South African Criminal Check'"
        :customerApplicationId="customerApplicationId"
      />
      <IncomeEstimatorReport
        v-else-if="service?.service_name === 'Income Estimator'"
        :income_estimator_data="payload"
      />
      <IncomePredictorReport
        v-else-if="service?.service_name === 'Income Predictor'"
        :income_predictor_data="payload"
      />
      <ReportViewer
        v-else-if="isCreditReportNamibia"
        :response_doc="namibiaPdfUrl"
        title="Credit Report Namibia"
      >
        <div class="alert alert-info text-xs">
          <i class="fas fa-spinner fa-spin me-2"></i>
          Document is being generated. Please wait...
        </div>
      </ReportViewer>

      <template v-else>
        <div v-if="hasCreditQuestions" class="question-fallback">
          <div class="card shadow-sm border rounded-3">
            <div class="card-body p-3">
              <h6 class="mb-2">{{ service?.service_name }} Responses</h6>
              <div class="row g-3">
                <div
                  class="col-md-6"
                  v-for="q in creditPayloadNormalized.questions"
                  :key="q.question_id || q.question"
                >
                  <div class="small text-muted">{{ q.question }}</div>
                  <div class="fw-semibold">
                    <template v-if="q.question_type === 'File' && q.response">
                      <a :href="q.response" target="_blank" rel="noopener"
                        >View file</a
                      >
                    </template>
                    <template v-else>
                      <template v-if="Array.isArray(q.response)">{{
                        q.response.join(", ")
                      }}</template>
                      <template v-else>{{ q.response ?? "N/A" }}</template>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="hasFormQuestions" class="question-fallback">
          <div class="card shadow-sm border rounded-3">
            <div class="card-body p-3">
              <h6 class="mb-2">{{ service?.service_name }} Responses</h6>
              <div class="row" :class="{ 'g-4': formQuestions.length > 3 }">
                <div
                  v-for="q in formQuestions"
                  :key="q.question_id || q.question"
                  class="col-md-6 mt-2 mb-2"
                >
                  <label class="form-label text-xs fw-bold d-block mb-1">{{
                    q.question
                  }}</label>

                  <div v-if="isFileResponse(q)">
                    <button
                      type="button"
                      class="btn btn-dark text-white rounded-small text-xs shadow text-uppercase"
                      @click="show_document(q.response)"
                    >
                      View Document
                    </button>
                    <div
                      v-if="selected_document_url === q.response"
                      class="mt-3"
                    >
                      <iframe
                        :src="q.response"
                        class="w-100 border-0 iframe-style"
                      ></iframe>
                    </div>
                  </div>

                  <div v-else-if="Array.isArray(format_questions(q.response))">
                    <div
                      v-for="(item, index) in format_questions(q.response)"
                      :key="index"
                      class="mb-2"
                    >
                      <input
                        type="text"
                        class="form-control text-xs rounded-small p-3 shadow-sm"
                        :value="item"
                        readonly
                      />
                    </div>
                  </div>

                  <div v-else-if="q.question_type === 'Text Area'">
                    <textarea
                      :class="[
                        'form-control',
                        'text-xs',
                        'rounded-small',
                        'p-3',
                        'shadow-sm',
                        'textarea-auto-height',
                      ]"
                      :value="q.response"
                      readonly
                      rows="6"
                    ></textarea>
                  </div>

                  <div v-else>
                    <input
                      type="text"
                      class="form-control text-xs rounded-small p-3 shadow-sm"
                      :value="q.response"
                      readonly
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="processedRows.length" class="table-responsive">
          <table class="table table-bordered">
            <tbody>
              <template v-for="(item, index) in processedRows" :key="index">
                <tr v-if="item.type === 'heading'">
                  <th class="table-secondary py-2 text-s" :colspan="2">
                    {{ format_label(item.label) }}
                  </th>
                </tr>

                <tr v-else-if="item.type === 'input' || item.type === 'label'">
                  <td class="col-3">
                    <span class="fw-bold text-xs me-2">{{
                      format_label(item.label)
                    }}</span>
                  </td>
                  <td class="col-9">
                    <span
                      v-if="item.value || item.value === 0"
                      class="text-xs"
                      >{{ format_auth_value(item) }}</span
                    >
                    <span v-else class="text-muted text-xs">None</span>
                  </td>
                </tr>

                <template v-else-if="item.type === 'custom_table'">
                  <tr>
                    <th
                      class="table-dark text-white text-s"
                      :colspan="item.headers.length"
                    >
                      {{ format_label(item.table) }}
                    </th>
                  </tr>
                  <tr>
                    <th
                      v-for="header in item.headers"
                      :key="header"
                      class="text-xxs bg-gray text-start"
                    >
                      {{ header }}
                    </th>
                  </tr>
                  <tr v-for="(row, rowIndex) in item.rows" :key="rowIndex">
                    <td
                      v-for="(val, key) in row"
                      :key="key"
                      class="text-xxs text-start"
                    >
                      {{ val }}
                    </td>
                  </tr>
                </template>

                <template
                  v-else-if="item.type === 'images' && item.images.length"
                >
                  <tr>
                    <td :colspan="2" class="text-center py-3">
                      <label class="form-label text-s fw-bold mb-3 d-block">{{
                        item.label
                      }}</label>
                      <div
                        class="d-flex justify-content-center align-items-center gap-3 mx-auto"
                      >
                        <div
                          v-for="(image, i) in item.images"
                          :key="i"
                          class="image-container text-center"
                        >
                          <p class="text-dark small mb-2">{{ image.label }}</p>
                          <img
                            :src="image.src"
                            class="facial-image img-fluid rounded shadow-sm"
                            :alt="image.label"
                          />
                        </div>
                        <div v-if="customer_image" class="image-container">
                          <p class="text-dark small mb-2">Customer Image</p>
                          <img
                            :src="customer_image"
                            class="facial-image img-fluid rounded shadow-sm"
                            alt="Customer Facial Image"
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </template>
            </tbody>
          </table>
        </div>

        <div v-else class="text-muted p-3">There is no service data.</div>
      </template>

      <div v-if="showSupportingDocuments && documentsList.length" class="mt-4">
        <button
          class="btn border border-2 text-white rounded-small fw-bold text-xs shadow-sm mb-3 d-flex align-items-center w-100 py-3"
          @click="is_documents_visible = !is_documents_visible"
        >
          <span class="me-2 text-dark">Supporting Documents</span>
          <span class="ms-auto text-dark">
            <i
              :class="{
                'fa fa-chevron-down': !is_documents_visible,
                'fa fa-chevron-up': is_documents_visible,
              }"
            ></i>
          </span>
        </button>

        <div v-show="is_documents_visible">
          <div
            v-for="document in documentsList"
            :key="document.id"
            class="mb-3"
          >
            <button
              type="button"
              class="btn btn-dark text-white rounded-small text-xs fw-bold shadow text-uppercase"
              @click="toggle_iframe(document)"
            >
              {{ document.document_type || "Document" }}
            </button>

            <div
              v-if="selected_document && selected_document.id === document.id"
              class="mt-3"
            >
              <div
                v-if="isValidDocumentUrl(selected_document.content)"
                class="ratio ratio-1x1"
              >
                <iframe
                  class="w-100 border-0"
                  :src="selected_document.content"
                ></iframe>
              </div>
              <div v-else class="text-muted text-xs">
                Invalid or unavailable document URL.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  serviceRules,
  defaultDataProcessing,
} from "@/utils/customer_data_rules.js";
import CompanyCreditReport from "@/layouts/pdf_reporting/company_credit_report/CompanyCreditReport.vue";
import AMLReport from "@/layouts/pdf_reporting/aml_reporting/AMLReport.vue";
import ReportViewer from "@/components/v2_report/ReportViewer.vue";
import OcrVerificationReport from "@/layouts/pdf_reporting/ocr_verification/OcrVerificationReport.vue";
import OcrLivenessVerificationReport from "@/layouts/pdf_reporting/ocr_liveness_verification/OcrLivenessVerificationReport.vue";
import IdVerificationReport from "@/layouts/pdf_reporting/id_liveness_verification/IdVerificationReport.vue";
import AlternativeLivenessReport from "@/layouts/pdf_reporting/alternative_verification/AlternativeVerificationReport.vue";
import EligibilitySolutionReport from "@/layouts/pdf_reporting/eligibility_solution/EligibilitySolutionVerificationReport.vue";
import BankStatementVerificationReport from "@/layouts/pdf_reporting/finch_bank_statement/BankStatementVerificationReport.vue";
import AddressVerificationReport from "@/layouts/pdf_reporting/address_verification/AddressVerificationReport.vue";
import AllInOneBankVerificationReport from "@/layouts/pdf_reporting/all_in_one_bank_verification/AllInOneBankVerificationReport.vue";
import RsaIdVerificationReport from "@/layouts/pdf_reporting/rsa_id_verification/RsaIdVerificationReport.vue";
import IndividualBankAccountReport from "@/layouts/pdf_reporting/bank_account_verification/individual_bank_account/IndividualBankAccountReport.vue";
import DirectorScreeningReport from "@/layouts/pdf_reporting/director_screening/DirectorScreeningReport.vue";
import CompanyBankAccountReport from "@/layouts/pdf_reporting/bank_account_verification/company_bank_account/CompanyBankAccountReport.vue";
import CreditReport from "@/layouts/pdf_reporting/credit_report/CreditReport.vue";
import RefugeeVerificationReport from "@/layouts/pdf_reporting/refugee_verification/RefugeeVerificationReport.vue";
import EnterpriseAmlVerificationReport from "@/layouts/pdf_reporting/enterprise_aml/EnterpriseAmlVerificationReport.vue";
import TrustSearchReport from "@/layouts/pdf_reporting/trust_search_report/TrustSearchReport.vue";
import MastercardReport from "@/layouts/pdf_reporting/mastercard_report/MastercardReport.vue";
import EnterpriseKYBReport from "@/layouts/pdf_reporting/enterprise_kyb/EnterpriseKYBReport.vue";
import ComprehensiveLivenessVerificationReport from "@/layouts/pdf_reporting/comprehensive_liveness_verification/ComprehensiveLivenessVerificationReport.vue";
import AcademicCheckManagement from "@/layouts/background_screening/academic_check/AcademicCheckManagement.vue";
import HuruCriminalCheckManagement from "@/layouts/background_screening/huru_criminal_check/HuruCriminalCheckManagement.vue";
import IncomeEstimatorReport from "@/layouts/pdf_reporting/income_estimator/IncomeEstimatorReport.vue";
import IncomePredictorReport from "@/layouts/pdf_reporting/income_predictor/IncomePredictorReport.vue";

export default {
  name: "ServiceData",
  components: {
    CompanyCreditReport,
    AMLReport,
    ReportViewer,
    OcrVerificationReport,
    OcrLivenessVerificationReport,
    IdVerificationReport,
    AlternativeLivenessReport,
    EligibilitySolutionReport,
    BankStatementVerificationReport,
    AddressVerificationReport,
    AllInOneBankVerificationReport,
    RsaIdVerificationReport,
    IndividualBankAccountReport,
    DirectorScreeningReport,
    CompanyBankAccountReport,
    CreditReport,
    RefugeeVerificationReport,
    EnterpriseAmlVerificationReport,
    TrustSearchReport,
    MastercardReport,
    EnterpriseKYBReport,
    ComprehensiveLivenessVerificationReport,
    AcademicCheckManagement,
    HuruCriminalCheckManagement,
    IncomeEstimatorReport,
    IncomePredictorReport,
  },
  data() {
    return {
      selected_document: null,
      is_documents_visible: false,
      selected_document_url: null,
    };
  },
  props: {
    service: {
      type: Object,
      required: false,
    },
    response: {
      type: [Object, Array, String, Number, Boolean, null],
      required: false,
      default: null,
    },
    response_doc: {
      type: String,
      required: false,
      default: null,
    },
    customer_image: {
      type: String,
      required: false,
      default: null,
    },
    customerApplicationId: {
      type: String,
      required: false,
      default: null,
    },
  },
  computed: {
    payload() {
      return this.response ?? this.service?.response ?? this.service;
    },
    normalizedPayload() {
      const p = this.payload;
      if (typeof p === "string") {
        try {
          return JSON.parse(p);
        } catch (e) {
          return p;
        }
      }
      return p;
    },
    creditPayload() {
      const p = this.normalizedPayload;
      const id = this.service?.service_id ?? this.service?.id;
      if (
        p &&
        Array.isArray(p.credit_bureau) &&
        id !== undefined &&
        id !== null
      ) {
        const found = p.credit_bureau.find(
          (it) => String(it.service_id ?? it.id) === String(id),
        );
        if (found) return found.response ?? found;
      }
      return null;
    },
    creditPayloadNormalized() {
      const cp = this.creditPayload;
      if (typeof cp === "string") {
        try {
          return JSON.parse(cp);
        } catch (e) {
          return cp;
        }
      }
      return cp;
    },
    hasCreditQuestions() {
      const qs =
        this.creditPayloadNormalized && this.creditPayloadNormalized.questions;
      return Array.isArray(qs) && qs.length > 0;
    },
    formQuestions() {
      const p = this.normalizedPayload;
      // If the payload is already sliced to just the questions
      // handle it directly first.
      const directQs =
        p && Array.isArray(p.questions)
          ? p.questions
          : p && p.response && Array.isArray(p.response.questions)
          ? p.response.questions
          : null;
      if (Array.isArray(directQs) && directQs.length) {
        return directQs.map(this._normalizeQuestionResponse);
      }

      const currentId = this.service?.service_id ?? this.service?.id;
      if (currentId === undefined || currentId === null) return [];

      let sysGen = p?.system_generated || p?.response?.system_generated || null;
      if (typeof sysGen === "string") {
        try {
          sysGen = JSON.parse(sysGen);
        } catch (e) {
          /* leave as string */
        }
      }
      if (!sysGen || typeof sysGen !== "object") return [];

      const services = Array.isArray(sysGen.services)
        ? sysGen.services
        : sysGen.services && typeof sysGen.services === "object"
        ? [sysGen.services]
        : [];
      if (!services.length) return [];

      const match = services.find((s) => String(s?.id) === String(currentId));
      if (!match || !Array.isArray(match.questions) || !match.questions.length)
        return [];

      return match.questions.map(this._normalizeQuestionResponse);
    },
    hasFormQuestions() {
      return Array.isArray(this.formQuestions) && this.formQuestions.length > 0;
    },
    processedRows() {
      const data = this.creditPayloadNormalized;
      if (!data || typeof data !== "object") return [];
      const rows = this.preprocess_data(
        data,
        "",
        this.service?.service_name || "",
      );
      return rows.filter((r) => {
        const key = r.key || "";
        const label = (r.label || "").toString().toLowerCase();
        const isDocKey =
          key.includes("documents") ||
          key.includes("document") ||
          label.includes("document");
        return !isDocKey;
      });
    },
    documentsList() {
      const docs = this.payload?.documents || this.service?.documents || [];
      return Array.isArray(docs) ? docs : [];
    },
    showSupportingDocuments() {
      const serviceName = (this.service?.service_name || "").toLowerCase();
      const allowedServices = [
        "cipc verification",
        "cipc plus score",
        "experian credit report (pdf)",
        "credit report namibia",
      ];
      return allowedServices.some((allowed) => serviceName.includes(allowed));
    },
    isCreditReportNamibia() {
      const serviceName = (this.service?.service_name || "").toLowerCase();
      return serviceName.includes("credit report namibia");
    },
    namibiaPdfUrl() {
      const d = this.payload;
      if (!d) return null;
      const url =
        d.pdf_url || d.url_link || d.URL_LINK || d.PDF_URL ||
        d.response?.url_link || d.response?.pdf_url ||
        (Array.isArray(d.result) && d.result.length
          ? d.result[0].URL_LINK || d.result[0].url_link || d.result[0].pdf_url
          : null);
      return url && typeof url === "string" && url.startsWith("http") ? url : null;
    },
    amlNormalized() {
      const res = this.payload || {};
      if (res.NmResults || res.PepResults || res.WlsResults) return res;
      const dv =
        res?.Fields?.Applicants_IO?.Applicant?.[0]?.Attributes
          ?.DSWatchlistVerification;
      if (dv) {
        return {
          RequestData: res.RequestData || {},
          NmResults: dv.NmResults || [],
          PepResults: dv.PepResults || [],
          WlsResults: dv.WlsResults || [],
        };
      }
      return res;
    },
    amlResponseInfo() {
      const res = this.payload || {};
      return res?.ResponseInfo || {};
    },
    amlDateScored() {
      const res = this.payload || {};
      const attrs =
        res?.Fields?.Applicants_IO?.Applicant?.[0]?.Attributes || {};
      return (
        attrs?.DRWatchlistVerification?.TimeOfRequest ||
        attrs?.DSWatchlistVerification?.TimeOfRequest ||
        ""
      );
    },
  },
  methods: {
    isFileResponse(q) {
      const resp = q && q.response;
      if (!resp || typeof resp !== "string") return q?.question_type === "File";
      return (
        q?.question_type === "File" ||
        resp.startsWith("https://s3.af-south-1.amazonaws.com/") ||
        resp.toLowerCase().includes(".pdf")
      );
    },
    show_document(url) {
      this.selected_document_url =
        this.selected_document_url === url ? null : url;
    },
    format_questions(questions) {
      if (typeof questions === "string") {
        try {
          questions = JSON.parse(questions.replace(/'/g, '"'));
        } catch (error) {
          // leave as string
        }
      }
      return questions;
    },
    _normalizeQuestionResponse(q) {
      let response = q?.response;
      response = this.format_questions(response);
      return { ...q, response };
    },
    toggle_iframe(document) {
      this.selected_document =
        this.selected_document && this.selected_document.id === document.id
          ? null
          : document;
    },
    isValidDocumentUrl(url) {
      if (!url || typeof url !== "string") return false;
      if (url.startsWith("data:application/pdf")) return true;
      try {
        const u = new URL(url);
        return u.protocol === "http:" || u.protocol === "https:";
      } catch (e) {
        return false;
      }
    },
    format_label(key) {
      if (typeof key !== "string" || !key.trim()) return "";
      return key
        .replace(/[_-]/g, " ")
        .replace(/[^a-zA-Z0-9]/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
        .replace(/\s+/g, " ")
        .trim()
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(" ");
    },
    preprocess_data(data, parent_key = "", service_name = "") {
      let result = [];
      const handledPaths = new Set();

      for (const rule of serviceRules) {
        const handled = rule.call(this, data, service_name);
        if (Array.isArray(handled) && handled.length > 0) {
          result.push(...handled);
          if (Array.isArray(rule.handledKeys)) {
            rule.handledKeys.forEach((key) => handledPaths.add(key));
          }
        }
      }

      const isHandled = (path) => {
        for (const handled of handledPaths) {
          if (
            path === handled ||
            path.startsWith(handled + ".") ||
            path.startsWith(handled + "[")
          ) {
            return true;
          }
        }
        return false;
      };
      const defaultResult = defaultDataProcessing(
        data,
        parent_key,
        service_name,
        this.format_label,
        isHandled,
      );
      result.push(...defaultResult);
      return result;
    },
    format_auth_value(item) {
      if (item.key && item.key.includes("document_authenticity")) {
        if (item.value === 1) return "Passed";
        if (item.value === 0) return "Failed";
        if (item.value === 2) return "Not Checked";
        return "Unknown";
      }
      return item.value ?? "None";
    },
  },
};
</script>

<style scoped>
.service-data-content pre {
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 360px;
  overflow: auto;
}
</style>
